import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  ALL_ROLES,
  ALL_VISIT_TYPES,
  CARE_LOCATIONS,
  DEFAULT_HOSPICE_ROLES,
  DEFAULT_PALLIATIVE_ROLES,
  HOPE_VISIT_TYPES,
  HOSPICE_VISIT_TYPES,
  LEVELS_OF_CARE,
  MISC_DISCIPLINES,
  PALLIATIVE_VISIT_TYPES,
  ROLE_LOOKUP_ABBREVIATION,
} from "configs/calendar-config";
import { LoadingController, ToastController } from "ionic-angular";
import LevelOfCare from "models/patient-chart/levelOfCare";
import Visit from "models/patient-chart/visit";
import moment from "moment-timezone";
import { Observable } from "rxjs/Observable";
import { of as ObservableOf } from "rxjs/observable/of";
import { _throw as ObservableThrow } from "rxjs/observable/throw";
import { catchError, map } from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import AssessmentRole from "../../models/assessment/assessment-role";
import AssessmentType from "../../models/assessment/assessment-type";
import Calendar from "../../models/calendar/calendar";
import CalendarAssignedTo from "../../models/calendar/calendar-assigned-to.ts";
import CalendarEvent from "../../models/calendar/calendar-event";
import CompanyRole from "../../models/company/company-role";
import { CompanyTeam } from "../../models/company/company-team";
import TeamMember from "../../models/company/team-member";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import { BasePage } from "../../pages/base-page";
import { ConfigurationService } from "../configuration/configuration.service";
import { StateManagementService } from "../state-management/state-management.service";

const TIMEZONE = moment.tz.guess();
const PAL_LOC = "Palliative";
const IS_BI_WEEKLY_REGEX = /\bevery Two Weeks\b/;

/**
 * NOVA-1806: shape of GET /assessment/credential-eligibility. `reason` is one of the
 * CredentialGateReasonEnum keys (credential_not_on_file / credential_expired /
 * credential_wrong_type) and is absent when eligible; `message` carries the server's own copy for
 * the reason, used as the localisation fallback.
 */
export interface CredentialEligibility {
  eligible: boolean;
  reason?: string;
  message?: string;
  /** PASS | WARN | BLOCK. Only BLOCK means the write would actually be refused. */
  outcome?: string;
  /** The visit type's requirement, for interpolation: "RN", "RN or LPN/LVN". */
  acceptedCredentials?: string;
}

/** Result of the shared Start-Visit credential pre-check: disable Start, and why. */
export interface CredentialPrecheckResult {
  block: boolean;
  warning: string;
}

/**
 * NOVA-1806: the reason keys assessment-service sends, on the eligibility response and in the
 * create 409 body. They double as the i18n keys in en.json, so one canonical message serves both
 * the inline pre-check and the server rejection.
 */
export const CREDENTIAL_REASON_KEYS: string[] = [
  "credential_not_on_file",
  "credential_expired",
  "credential_wrong_type",
];

/**
 * Nothing to block on and nothing to say. Returned when the check passes, when there is no visit
 * type to check yet, and when the lookup fails — assessment-service is the enforcement point, so a
 * failed pre-check must never wedge the workflow, only lose the courtesy message.
 *
 * (Named NO_CREDENTIAL_BLOCK before NOVA-1806's re-land, which read as "block because there is no
 * credential" — the exact opposite of its value.)
 */
const CREDENTIAL_CHECK_CLEAR: CredentialPrecheckResult = {
  block: false,
  warning: "",
};

const CREDENTIAL_OUTCOME_BLOCK = "BLOCK";
const CREDENTIAL_OUTCOME_WARN = "WARN";

@Injectable()
export class CalendarService extends BasePage {
  // dataUrl: string;
  public _subject = new Subject<object>();
  public calNotifCount = this._subject.asObservable();
  newCalNotifications: number = 0;
  idgDataUrl: string;
  loggedInUserEmail: string;
  asmtUrl: string;
  spinner = null;
  spinnerCounter = 0;

  casDataUrl: string; //need to remove
  schedulingUrl: string;
  showDeathVisitToggle: boolean = false;

  constructor(
    public http: HttpClient,
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    public stateData: StateManagementService,
    private config: ConfigurationService, // private authSvc: AuthService,
    // NOVA-1806: the shared credential pre-check resolves its own message here, so every Start
    // Visit surface gets identical wording without each one re-deriving it.
    public translate: TranslateService
  ) {
    super(stateData);
    if (config.getConfig().isLocal) {
      this.casDataUrl = config.getConfig().localEndpoints.calendar;
    } else {
      this.casDataUrl = config.getConfig().apiUrl;
    }
    this.idgDataUrl = config.resolveIdgUrl();

    this.schedulingUrl = config.getConfig().apiUrl + "/scheduling";

    this.asmtUrl = config.getConfig().apiUrl;
    this.stateData.showDeathVisitToggle.subscribe((toggle) => {
      this.showDeathVisitToggle = toggle;
    });
  }

  /**
   * Get the team role lookups
   */
  getTeamRoles(): Observable<CompanyRole[]> {
    return this.http
      .get<CompanyRole[]>(`${this.idgDataUrl}/company-resources/roles?type=H`)
      .map((x) => {
        let roles: CompanyRole[] = [];
        for (let i = 0; i < x.length; i++) {
          let o = x[i];
          roles.push({
            display: o["name"],
            id: o["id"],
            lookup: o["abbreviation"],
          });
        }

        return roles;
      });
  }

  getAllUsersByRole(
    roleId: number,
    onlySystemUser: boolean = false,
    onlyActiveUsers = false
  ): Observable<ContentWrapper<TeamMember>> {
    return this.http
      .get<ContentWrapper<TeamMember>>(
        `${this.idgDataUrl}/company-resources/persons/role/${roleId}?size=9999&onlySystemUsers=${onlySystemUser}&onlyActiveUsers=${onlyActiveUsers}`
      )
      .pipe(
        map((response) => {
          const persons: ContentWrapper<TeamMember> =
            new ContentWrapper<TeamMember>();
          persons.content = response.content.filter(
            (o) => o && o.discipline && o.discipline !== ""
          );
          persons.numberOfElements = persons.content.length;
          persons.number = persons.content.length;
          return persons;
        })
      );
  }

  getTeamMembersTyped(
    teamId: number,
    role: string
  ): Observable<ContentWrapper<TeamMember>> {
    return this.http
      .get<ContentWrapper<TeamMember>>(
        `${this.idgDataUrl}/company-resources/teams/${teamId}/members?size=9999`
      )
      .map((x) => {
        let cw: ContentWrapper<TeamMember> = new ContentWrapper<TeamMember>();
        let u: TeamMember[] = [];
        for (let i = 0; i < x.content.length; i++) {
          let o = x.content[i];
          for (let j = 0; j < o.roles.length; j++) {
            let r = o.roles[j];
            if (r === role) {
              u.push(o);
              break;
            }
          }
        }

        cw.content = u;
        cw.numberOfElements = u.length;
        cw.number = u.length;
        return cw;
      });
  }

  // replacement of getUnassignedOrders
  getUnassignedVisits(request: any) {
    return this.http.post(this.schedulingUrl + "/search", request);
  }

  /**
   * Returns all open unassigned orders for a given tenant
   */


  saveEvent(event: CalendarEvent): Observable<CalendarEvent> {
    event.dataType = "EVENT";
    let list: CalendarEvent[] = [event];
    return this.http.post<CalendarEvent>(this.schedulingUrl + "/event", list);
  }

  saveUnscheduledEvent(uoList: any[]): Observable<any> {
    return this.http.post<any>(this.schedulingUrl + "/event", uoList);
  }


  getCalendarByUser(): Observable<Calendar> {
    return this.http.get<Calendar>(this.schedulingUrl + "/user");
  }

  makeNewCalendarEvent(x: CalendarEvent): CalendarEvent {
    let ce = new CalendarEvent();

    ce.activityNote = x.activityNote;
    ce.assignedTo = x.assignedTo;
    ce.assignedToFullName = x.assignedToFullName;
    ce.assignedToUsername = x.assignedToUsername;
    ce.disciplineCode = x.disciplineCode;
    ce.role = x.role;
    ce.eventId = x.eventId;
    ce.patientChartId = x.patientChartId;
    ce.patientName = x.patientName;
    ce.patientFirstName = x.patientFirstName;
    ce.patientLastName = x.patientLastName;
    ce.patientMiddleInitial = x.patientMiddleInitial;
    ce.recurringEventId = x.recurringEventId;
    ce.userId = x.userId;
    ce.visitId = x.visitId;
    ce.status = x.status;
    ce.visitType = x.visitType;
    ce.visitTypeName = x.visitTypeName;
    ce.timezone = x.timezone;
    ce.day = x.day;
    ce.week = x.week;
    ce.month = x.month;
    ce.year = x.year;
    ce.startDatetime = this.formatDateFromTimeZone(x.startDatetime);
    ce.endDatetime = this.formatDateFromTimeZone(x.endDatetime);
    ce.createdBy = x.createdBy;
    ce.createdDatetime = this.formatCreateUpdateDateFromTimeZone(
      x.createdDatetime
    );
    ce.updatedBy = x.updatedBy;
    ce.updatedDatetime = this.formatCreateUpdateDateFromTimeZone(
      x.updatedDatetime
    );
    ce.missedVisit = x.missedVisit;
    ce.missedVisitReason = x.missedVisitReason;
    ce.missedVisitDescription = x.missedVisitDescription;
    ce.careLocation = x.careLocation;
    ce.zipCode = x.zipCode;
    ce.patientPhone = x.patientPhone;
    ce.isDelete = x.isDelete;
    ce.activity = x.activity;
    ce.eventType = x.eventType;
    ce.orderId = x.orderId;
    ce.orderCreatedDate = x.orderCreatedDate;
    ce.orderDescription = x.orderDescription;
    ce.orderProvider = x.orderProvider;
    ce.orderStartDate = x.orderStartDate;
    ce.orderDiscontinueDate = x.orderDiscontinueDate;
    ce.hopeFlags = x.hopeFlags;
    ce.isSfv = x.isSfv;
    ce.isHuv1 = x.isHuv1;
    ce.isHuv2 = x.isHuv2;
    ce.prnReason = x.prnReason;

    return ce;
  }

  getAssignedToByIDAndCompanyId(
    assignedToID: number,
    companyId: number
  ): Observable<CalendarAssignedTo> {
    return this.http.get<CalendarAssignedTo>(
      `${this.casDataUrl}/company-resources/${assignedToID}?useExternalId=true&companyId=${companyId}`
    );
  }

  getPatientEventStatus(patientChartId): Observable<any> {
    return this.http.get<any>(
      `${this.casDataUrl}/patient-charts/${patientChartId}/visits`
    );
  }

  // replacement of loadViewEvents
  getSchedulingEvents(request: any): Observable<any> {
    return this.http.post(this.schedulingUrl + "/search", request).pipe(
      catchError((err) => {
        console.error("Error occurred while fetching scheduling events:", err);
        return ObservableThrow(err); // Manejo de errores con throwError
      })
    );
  }

  // Get Task details counts for dashboard
  getSchedulingCounts(request: any): Observable<any> {
    return this.http.post(this.schedulingUrl + "/count", request).pipe(
      catchError((err) => {
        console.error("Error occurred while fetching scheduling counts:", err);
        return ObservableThrow(err);
      })
    );
  }

  formatSchedulingResponse(hits: any): CalendarEvent[] {
    let events: CalendarEvent[] = [];
    if (hits) {
      hits.forEach((x: CalendarEvent) => {
        events.push(this.makeNewCalendarEvent(x));
      });
    }
    return events;
  }

  /**
   * Returns all of the company teams associated w/ this tenant
   */
  getAllTeamsTyped(): Observable<CompanyTeam[]> {
    let url = `${this.idgDataUrl}/company-resources/teams/allTeams`;
    return this.http.get<CompanyTeam[]>(url);
  }

  getCreateVisitModalData(patientChartId: number): Observable<any> {
    return new Observable((observer) => {
      try {
        let url = `${this.asmtUrl}/patient-charts/${patientChartId}/visit-types`;
        this.http.get(url).subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {
        console.error("Error during data submission and building.", error);
      }
    });
  }

  /**
   * NOVA-1806 (#4/#5): asks the server whether the documenting (logged-in) user holds a valid
   * credential for the selected visit type — i.e. whether a create POST would pass the server
   * credential gate. Used to disable Start before POST. Keyed on the visit type only (the rule is
   * seeded per type, role-independent); server-evaluated on the logged-in user, so the pre-check
   * can never disagree with the gate.
   *
   * <p>`reason` / `message` are present only when ineligible.
   */
  getCredentialEligibility(
    asmtTypeLookupCode: string,
    roleLookupCode?: string
  ): Observable<CredentialEligibility> {
    // SUP-20053: the role narrows the check server-side. It is appended only when we actually
    // have it — the parameter is optional on the server, where an absent role means "evaluate as
    // before" rather than "check nothing". Omitting it on a surface that does not know the role is
    // therefore safe, and never silently disables the gate.
    const role = roleLookupCode
      ? `&roleLookupCode=${encodeURIComponent(roleLookupCode)}`
      : "";
    const url =
      `${this.asmtUrl}/assessment/credential-eligibility` +
      `?asmtTypeLookupCode=${encodeURIComponent(asmtTypeLookupCode)}` +
      role;
    return this.http.get<CredentialEligibility>(url);
  }

  /**
   * NOVA-1806: THE Start-Visit credential pre-check. Every entry point calls this one function —
   * the calendar event view, the patient-chart visit grid, and the create-visit modal.
   *
   * Two separately-maintained copies of this logic are what let the modal go unguarded: the visit
   * grid got the check and the modal never did, so the role bypass survived even in the
   * "client-side fixed" state. Keeping it here means a new Start Visit surface inherits the check
   * instead of having to remember it.
   *
   * Fails open by design — assessment-service is the enforcement point and rejects with 409. A
   * lookup error must never wedge the workflow, only lose the courtesy warning.
   */
  checkVisitTypeCredential(
    asmtTypeLookupCode: string,
    roleLookupCode?: string
  ): Observable<CredentialPrecheckResult> {
    if (!asmtTypeLookupCode) {
      return ObservableOf(CREDENTIAL_CHECK_CLEAR);
    }
    // .pipe(map, catchError) rather than the patchable .map()/.catch(): this file only imports the
    // rxjs/operators forms, and `catch` is not patched onto Observable here (idg-data-service has to
    // import "rxjs/add/operator/catch" to use it).
    return this.getCredentialEligibility(asmtTypeLookupCode, roleLookupCode).pipe(
      map((res: CredentialEligibility) => {
        if (!res || res.eligible !== false) {
          return CREDENTIAL_CHECK_CLEAR;
        }
        // NOVA-1806: a failed check no longer implies the write would be refused. Under the
        // approved matrix most failures on the mixed-discipline visit types are a WARN — the
        // message is shown and Start stays enabled. Only BLOCK disables it. Treating every
        // failure as blocking is what stopped social workers, chaplains and aides documenting
        // routine care in QA and forced the July rollback.
        return {
          block: res.outcome === CREDENTIAL_OUTCOME_BLOCK,
          warning: this.credentialWarningFor(res),
        };
      }),
      catchError(() => ObservableOf(CREDENTIAL_CHECK_CLEAR))
    );
  }

  /**
   * NOVA-1806: resolve the one canonical message for a rejection reason, so the inline pre-check
   * and the server's 409 can never word the same situation differently.
   *
   * Prefers the i18n key (tenants can localise it) and falls back to the copy the server sent.
   * That fallback is load-bearing during the window between the assessment-service deploy and this
   * bundle reaching a tenant: the reason keys exist server-side before `en.json` has them, and
   * ngx-translate returns the key itself when it has no entry.
   */
  /**
   * NOVA-1806: the expired-licence copy differs between blocking and warning. The approved sentence
   * ends "...before you can document this visit type", which is true when we refuse the write and
   * false when we warn and then let the clinician continue — so the warn case uses the same
   * sentence without that clause. Every other reason reads correctly either way and uses one key.
   */
  private credentialI18nKeyFor(res: CredentialEligibility): string {
    if (
      res.reason === "credential_expired" &&
      res.outcome === CREDENTIAL_OUTCOME_WARN
    ) {
      return "credential_expired_warn";
    }
    return res.reason;
  }

  credentialWarningFor(res: CredentialEligibility): string {
    if (res && res.reason) {
      const key = this.credentialI18nKeyFor(res);
      // NOT_ON_FILE names the visit type's actual requirement, so the string is interpolated
      // rather than hardcoding "RN or LPN/LVN" — which was wrong on the RN-only types, where an
      // LPN/LVN never qualified. Reasons without the placeholder ignore the param.
      const translated = this.translate.instant(key, {
        credentials: res.acceptedCredentials || "RN or LPN/LVN",
      });
      if (translated && translated !== key) {
        return translated;
      }
    }
    if (res && res.message) {
      return res.message;
    }
    return this.translate.instant("credentialNotAcceptedForVisitType");
  }

  getStartVisitData(patientChartId: number): Observable<AssessmentRole[]> {
    let url = `${this.asmtUrl}/patient-charts/${patientChartId}/visit-types`;
    return this.http.get<AssessmentRole[]>(url).map((x) => {
      let types: AssessmentRole[] = [];
      let unmappedTypes = x["assessment_roles"];
      if (!unmappedTypes) return types;

      // iterate through the return values and create roles
      for (let i = 0; i < unmappedTypes.length; i++) {
        let role = new AssessmentRole(
          unmappedTypes[i]["role_lookup_code"],
          unmappedTypes[i]["role_lookup_value"]
        );

        // iterate through the return values and create types and add them to roles
        if (unmappedTypes[i]["assessment_types"]) {
          for (
            let j = 0;
            j < unmappedTypes[i]["assessment_types"].length;
            j++
          ) {
            let type = new AssessmentType(
              unmappedTypes[i]["assessment_types"][j]["sort_seq"],
              unmappedTypes[i]["assessment_types"][j]["createdDate"],
              unmappedTypes[i]["assessment_types"][j]["asmt_type_lookup_code"],
              unmappedTypes[i]["assessment_types"][j]["asmt_type_lookup_value"]
            );

            role.addType(type);
          }
        }

        types.push(role);
      }

      return types;
    });
  }

  createVisitFromCalenderEvent(data: Visit): Observable<Visit> {
    return new Observable<Visit>((observer) => {
      this.http.post(`${this.asmtUrl}/visits/fromCalendar`, data).subscribe(
        (data: Visit) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      //for testing, I will delete it when I check that everything is fine
      return;
    }
    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  public increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  public decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  formatDateFromTimeZone(date) {
    if (!date) {
      return date;
    }

    const incomingMoment = moment(date);
    const incomingOffset = incomingMoment.utcOffset();
    const targetOffset = moment.tz(incomingMoment, TIMEZONE).utcOffset();

    // Check if the offsets are the same. This is the most reliable check.
    const isSameTimezone = incomingOffset === targetOffset;

    if (isSameTimezone) {
      // If the offsets are the same, just format the original moment.
      return moment.parseZone(date).format("YYYY-MM-DD HH:mm");
    } else {
      // If they're different, convert and format.
      return incomingMoment.tz(TIMEZONE).format("YYYY-MM-DD HH:mm");
    }
  }

  formatCreateUpdateDateFromTimeZone(date) {
    if (date) {
      return moment(date).tz(TIMEZONE).format("YYYY-MM-DD HH:mm");
    } else {
      return date;
    }
  }

  formatToUtcDatetime(date) {
    if (date) {
      return moment.utc(moment(date), "YYYY-MM-DD HH:mm").format();
    } else {
      return date;
    }
  }

  formatLocalWithOffset(localDateString: string) {
    return moment(localDateString).format("YYYY-MM-DDTHH:mm:ssZ");
  }

  utcDateToHopeFormat(date) {
    if (date) {
      return moment.utc(date).format("MM/DD/YYYY");
    } else {
      return date;
    }
  }

  getDateInfo(date) {
    let info: any = {};
    let utcDate = moment.utc(date);

    info.day = moment(date).format("DD");
    info.week = moment(date).week().toString();
    info.month = (moment(date).month() + 1).toString();
    info.year = moment(date).year().toString();
    info.dayStart = this.startEndOf(date, "day", "startOf");
    info.dayEnd = this.startEndOf(date, "day", "endOf");
    info.weekStart = this.startEndOf(date, "week", "startOf");
    info.weekEnd = this.startEndOf(date, "week", "endOf");
    info.monthStart = this.startEndOf(date, "month", "startOf");
    info.monthEnd = this.startEndOf(date, "month", "endOf");
    info.currentDate = moment(utcDate).format("YYYY-MM-DD");

    return info;
  }

  startEndOf(date, period: string, type: string) {
    let ofDate = moment(date)[type](period).format();
    let utcDate = this.formatToUtcDatetime(ofDate);
    return utcDate;
  }

  getDateFormatWithUtc(date: Date, dateFormat: boolean = false): Date {
    if (dateFormat) return moment(date).format("MM/DD/YYYY");
    return moment(date).format("YYYY-MM-DD");
  }

  notWithinOrderTimeInterval(
    startDate,
    endDate,
    newEvent: CalendarEvent
  ): boolean {
    let newEventWeek = moment(newEvent.startDatetime).week();
    if (
      newEvent.orderDescription &&
      newEvent.orderDescription.toLowerCase().includes("day".toLowerCase())
    ) {
      let startDateTimeDay = moment(startDate).day();
      let endDateTimeDay = moment(endDate).day();
      const newEventStartDay = moment(newEvent.startDatetime).day();
      const newEventEndDay = moment(newEvent.endDatetime).day();

      if (newEventStartDay !== newEventEndDay) {
        return (
          (startDateTimeDay !== newEventStartDay &&
            startDateTimeDay !== newEventEndDay) ||
          (endDateTimeDay !== newEventStartDay &&
            endDateTimeDay !== newEventEndDay)
        );
      } else if (
        startDateTimeDay === newEventStartDay &&
        startDateTimeDay === newEventEndDay
      ) {
        return false;
      }
      return (
        newEvent.day !== startDateTimeDay || newEvent.day !== endDateTimeDay
      );
    } else if (
      newEvent.orderDescription &&
      newEvent.orderDescription.toLowerCase().includes("Week".toLowerCase())
    ) {
      let startDateTimeWeek = moment(startDate).week();
      let endDateTimeWeek = moment(endDate).week();
      const regex = new RegExp(`\\bTwo Weeks\\b`);
      if (regex.test(newEvent.orderDescription)) {
        const currentAndNextWeek = [newEventWeek, newEventWeek + 1];
        return (
          !currentAndNextWeek.includes(startDateTimeWeek) ||
          !currentAndNextWeek.includes(endDateTimeWeek)
        );
      } else {
        return (
          newEventWeek !== startDateTimeWeek || newEventWeek !== endDateTimeWeek
        );
      }
    } else if (
      newEvent.orderDescription &&
      newEvent.orderDescription.toLowerCase().includes("month".toLowerCase())
    ) {
      let startDateTimeMonth = moment(startDate).month() + 1;
      let endDateTimeMonth = moment(endDate).month() + 1;
      return (
        newEvent.month !== startDateTimeMonth ||
        newEvent.month !== endDateTimeMonth
      );
    } else {
      return false;
    }
  }

  mapCalendarAsessmentRoles(
    orderDiscipline: string,
    roles: CompanyRole[]
  ): CompanyRole[] {
    let assessmentRoles: AssessmentRole[] =
      this.initAssessmentRolesByDiscipline(orderDiscipline);
    let companyRoles: CompanyRole[] = [];

    assessmentRoles.forEach((role) => {
      const lookupAbbreviation = Object.keys(ROLE_LOOKUP_ABBREVIATION).find(
        (key) => {
          return ROLE_LOOKUP_ABBREVIATION[key] === role.lookupCode;
        }
      );
      companyRoles.push(this.getCompanyRoleByLookup(lookupAbbreviation, roles));
    });
    companyRoles = companyRoles.sort((a, b) =>
      a.display.localeCompare(b.display)
    );

    return companyRoles;
  }

  getCompanyRoleByLookup(
    lookupAbbreviation: string,
    roles: CompanyRole[]
  ): CompanyRole {
    return roles.find((role) => role.lookup === lookupAbbreviation);
  }

  initAssessmentRolesByDiscipline(orderDiscipline: string): AssessmentRole[] {
    let roles: AssessmentRole[] = [];
    switch (orderDiscipline) {
      case ALL_ROLES.LOOKUP_VALUE.SPIRITUAL_ADVISOR:
        roles = [
          ALL_ROLES.ASSESSMENT_ROLE.SPIRITUAL_ADVISOR,
        ] as AssessmentRole[];
        break;
      case ALL_ROLES.LOOKUP_VALUE.SOCIAL_WORKER:
        roles = [ALL_ROLES.ASSESSMENT_ROLE.SOCIAL_WORKER] as AssessmentRole[];
        break;
      case MISC_DISCIPLINES.LOOKUP_VALUE.HOSPICE_AID:
      case ALL_ROLES.LOOKUP_VALUE.HOSPICE_AIDE:
        roles = [
          ALL_ROLES.ASSESSMENT_ROLE.HOSPICE_AIDE,
          ALL_ROLES.ASSESSMENT_ROLE.LICENSED_NURSE,
          ALL_ROLES.ASSESSMENT_ROLE.REGISTERED_NURSE_RN,
          ALL_ROLES.ASSESSMENT_ROLE.NURSE_PRACTITIONER,
        ] as AssessmentRole[];
        break;
      case MISC_DISCIPLINES.LOOKUP_VALUE.SKILLED_NURSING:
      case MISC_DISCIPLINES.LOOKUP_VALUE.SKILLED_NURSE:
        roles = [
          ALL_ROLES.ASSESSMENT_ROLE.LICENSED_NURSE,
          ALL_ROLES.ASSESSMENT_ROLE.REGISTERED_NURSE_RN,
          ALL_ROLES.ASSESSMENT_ROLE.NURSE_PRACTITIONER,
        ] as AssessmentRole[];
        break;
      case MISC_DISCIPLINES.LOOKUP_VALUE.BEREAVEMENT:
        {
          roles = [
            ALL_ROLES.ASSESSMENT_ROLE.BEREAVEMENT_COUNSELOR,
          ] as AssessmentRole[];
        }
        break;
    }
    return roles;
  }

  initAssessmentRoles(
    levelOfCare: LevelOfCare,
    careType: string
  ): AssessmentRole[] {
    let roles: AssessmentRole[];
    if (levelOfCare && levelOfCare["levelOfCare"]) {
      if (
        levelOfCare["levelOfCare"].description === PAL_LOC &&
        levelOfCare.careLocationType &&
        levelOfCare.careLocationType.description
      ) {
        roles = this.getPalliativeRoles(
          levelOfCare.careLocationType.description
        );
      } else {
        roles = this.getHospiceRoles(levelOfCare["levelOfCare"].description);
      }
    } else {
      // get all roles/types based on Care Type
      roles =
        careType && careType === PAL_LOC
          ? this.getPalliativeRoles(null)
          : this.getHospiceRoles(null);
    }
    return roles;
  }

  getPalVisitTypes(): AssessmentType[] {
    return PALLIATIVE_VISIT_TYPES.map((visitType) => ({
      lookupCode: visitType.lookupCode,
      lookupValue: visitType.lookupValue,
    })) as AssessmentType[];
  }

  getDefaultPalliativeRoles(): AssessmentRole[] {
    return DEFAULT_PALLIATIVE_ROLES.map((role) => ({
      lookupCode: role.lookupCode,
      lookupValue: role.lookupValue,
      types: this.getPalVisitTypes(),
    })) as AssessmentRole[];
  }

  getPalliativeRoles(careLocationType: string): AssessmentRole[] {
    let paliativeRoles: AssessmentRole[] = this.getDefaultPalliativeRoles();
    if (careLocationType) {
      paliativeRoles.forEach((role) => {
        let visitTypeCodesToRemove: string[] = [];
        switch (careLocationType) {
          case CARE_LOCATIONS.HOME:
          case CARE_LOCATIONS.ASSISTED_LIVING_FACILITY:
            visitTypeCodesToRemove.push(
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.NEW_PATIENT
                .NURSING_HOME_SKILLED_NURSING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.ESTABLISHED_PATIENT
                .NURSING_HOME_SKILLED_NURSING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.HOSPITAL.FOLLOW_UP_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.HOSPITAL.INITIAL_VISIT
            );
            break;
          case CARE_LOCATIONS.NURSING_HOME:
          case CARE_LOCATIONS.SKILLED_NURSING_FACILITY:
            visitTypeCodesToRemove.push(
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.NEW_PATIENT
                .HOME_ASSISTED_LIVING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.ESTABLISHED_PATIENT
                .HOME_ASSISTED_LIVING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.HOSPITAL.FOLLOW_UP_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.HOSPITAL.INITIAL_VISIT
            );
            break;
          case CARE_LOCATIONS.HOSPITAL:
            visitTypeCodesToRemove.push(
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.NEW_PATIENT
                .HOME_ASSISTED_LIVING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.ESTABLISHED_PATIENT
                .HOME_ASSISTED_LIVING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.NEW_PATIENT
                .NURSING_HOME_SKILLED_NURSING,
              ALL_VISIT_TYPES.LOOKUP_CODE.PALLIATIVE.ESTABLISHED_PATIENT
                .NURSING_HOME_SKILLED_NURSING
            );
            break;
        }
        this.removeTypes(role, visitTypeCodesToRemove);
      });
    }
    return paliativeRoles;
  }

  getHosVisitTypes(): AssessmentType[] {
    return HOSPICE_VISIT_TYPES.map((visitType) => ({
      lookupCode: visitType.lookupCode,
      lookupValue: visitType.lookupValue,
    })) as AssessmentType[];
  }

  getDefaulHospiceRoles(): AssessmentRole[] {
    return DEFAULT_HOSPICE_ROLES.map((role) => ({
      lookupCode: role.lookupCode,
      lookupValue: role.lookupValue,
      types: this.getHosVisitTypes(),
    })) as AssessmentRole[];
  }

  getHospiceRoles(levelOfCare: string): AssessmentRole[] {
    let hospiceRoles: AssessmentRole[] = this.getDefaulHospiceRoles();
    if (levelOfCare) {
      hospiceRoles.forEach((role) => {
        let visitTypeCodes: string[];
        if (
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.CASE_MANAGER ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.TEAM_LEAD ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.REGISTERED_NURSE_RN ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.NURSE_PRACTITIONER
        ) {
          visitTypeCodes =
            role.lookupCode === ALL_ROLES.LOOKUP_CODE.NURSE_PRACTITIONER
              ? [ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT]
              : [ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT];
          if (
            role.lookupCode === ALL_ROLES.LOOKUP_CODE.CASE_MANAGER ||
            role.lookupCode === ALL_ROLES.LOOKUP_CODE.TEAM_LEAD
          ) {
            visitTypeCodes.push(
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD
            );
          }
          if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.TEAM_LEAD) {
            visitTypeCodes.push(
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT
            );
          }
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
          }
        } else if (
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.SPIRITUAL_ADVISOR ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.BEREAVEMENT_COUNSELOR
        ) {
          visitTypeCodes = [
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
          ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE);
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT
              );
              break;
          }
        } else if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.HOSPICE_AIDE) {
          visitTypeCodes = [
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE
              .INITIAL_COMPREHENSIVE_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.COMPREHENSIVE_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.EMERGENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.PHONE_CALL,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
          ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
          }
        } else if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.SOCIAL_WORKER) {
          visitTypeCodes = [
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
          ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE);
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
          }
        } else if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.MEDICAL_DIRECTOR) {
          visitTypeCodes = [
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
          ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
          }
        } else if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.LICENSED_NURSE) {
          visitTypeCodes = [
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE
              .INITIAL_COMPREHENSIVE_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.COMPREHENSIVE_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
            ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
          ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
            case LEVELS_OF_CARE.LOOKUP_VALUE.RESPITE_CARE:
            case LEVELS_OF_CARE.LOOKUP_VALUE.ROUTINE_HOME_CARE:
              visitTypeCodes.push(
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
                ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER
              );
              break;
          }
        } else if (
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.HOME_MAKER ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.VOLUNTEER_COORDINATOR ||
          role.lookupCode === ALL_ROLES.LOOKUP_CODE.VOLUNTEER
        ) {
          if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.HOME_MAKER)
            visitTypeCodes = [
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE
                .INITIAL_COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.EMERGENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.PHONE_CALL,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
            ];
          else if (
            role.lookupCode === ALL_ROLES.LOOKUP_CODE.VOLUNTEER_COORDINATOR
          )
            visitTypeCodes = [
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE
                .INITIAL_COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.EMERGENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.EMC,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.PHONE_CALL,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
            ];
          else if (role.lookupCode === ALL_ROLES.LOOKUP_CODE.VOLUNTEER)
            visitTypeCodes = [
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE
                .INITIAL_COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.COMPREHENSIVE_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.FACE_TO_FACE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.CONTINUOUS_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.EMERGENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.IN_PATIENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.WATCH_CARE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.SUPERVISORY_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.INITIAL_NURSING_ASSESSMENT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_COMPREHENSIVE,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_HOUSE_ENCOUNTER,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.HOSPICE_AIDE_ROUTINE_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT,
              ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.LCD,
            ];
          switch (levelOfCare) {
            case LEVELS_OF_CARE.LOOKUP_VALUE.GENERAL_IN_PATIENT:
            case LEVELS_OF_CARE.LOOKUP_VALUE.CONTINUOUS_CARE:
              visitTypeCodes.push(ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.ROUTINE);
              break;
          }
        }
        this.removeTypes(role, visitTypeCodes);
      });
    }
    return hospiceRoles;
  }

  removeTypes(role: AssessmentRole, lookupCodes: string[]): void {
    if (!this.showDeathVisitToggle) {
      if (
        !lookupCodes.includes(ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT)
      ) {
        lookupCodes.push(ALL_VISIT_TYPES.LOOKUP_CODE.HOSPICE.DEATH_VISIT);
      }
    }
    let types = role.types;
    const newTypes = types.filter((type) => {
      return !lookupCodes.includes(type.lookupCode);
    });
    role.types = newTypes;
  }

  mapHopeType(event: any): string {
    if (!event) return "";

    const types: string[] = [];
    if (event.isSfv) {
      types.push(HOPE_VISIT_TYPES.SYMPTOM_FOLLOW_UP);
    }

    if (event.isHuv1 && !event.isHuv2) {
      types.push(HOPE_VISIT_TYPES.HOPE_UPDATE_VISIT_1);
    } else if (event.isHuv2 && !event.isHuv1) {
      types.push(HOPE_VISIT_TYPES.HOPE_UPDATE_VISIT_2);
    }
    return types.join(", ");
  }

  eventNotInSameTwoWeeks(
    newEvent: CalendarEvent,
    dragEvent: CalendarEvent,
    eventList
  ): boolean {
    // --- 1. Guard Clauses: Exit early if conditions aren't met ---
    if (
      !newEvent.orderId ||
      !dragEvent ||
      !IS_BI_WEEKLY_REGEX.test(newEvent.orderDescription)
    ) {
      return false; // This check doesn't apply, so the move is valid.
    }

    // --- 2. Setup: Extract necessary data and dates ---
    const { firstNumber: maxEventsPerWeek } = this.extractNumbers(
      newEvent.orderDescription
    );
    const newEventStartTime = moment(newEvent.startDatetime);
    const originalEventStartTime = moment(dragEvent.startDatetime);
    const orderStartDate = moment(newEvent.orderStartDate);

    // --- 3. Frequency Check: Ensure the new week doesn't exceed the event limit ---
    const newEventWeek = newEventStartTime.week();
    const relatedEvents = eventList.filter(
      (event) => event.orderId === newEvent.orderId
    );

    const eventsInTargetWeek = relatedEvents.filter(
      (event) =>
        event.eventId !== newEvent.eventId &&
        moment(event.startDatetime).week() === newEventWeek
    ).length;

    if (eventsInTargetWeek + 1 > maxEventsPerWeek) {
      return true; // Invalid move: Exceeds max events per week.
    }

    // --- 4. NEW: Special Case for moving an event back ---
    // This allows an event that was moved one week ahead of its intended bi-weekly slot
    // (order_start_week + 2) to be moved back into that slot.
    const startOfWeekForOrder = orderStartDate.clone().startOf("week");
    const startOfWeekForOriginalEvent = originalEventStartTime
      .clone()
      .startOf("week");
    const startOfWeekForNewEvent = newEventStartTime.clone().startOf("week");

    // Check if the event's current position is 3 weeks after the order start.
    const isCurrentlyThreeWeeksOut =
      startOfWeekForOriginalEvent.diff(startOfWeekForOrder, "weeks") === 3;
    // Check if the target position is 2 weeks after the order start.
    const isMovingToTwoWeeksOut =
      startOfWeekForNewEvent.diff(startOfWeekForOrder, "weeks") === 2;

    if (isCurrentlyThreeWeeksOut && isMovingToTwoWeeksOut) {
      return false; // VALID move: This is an allowed corrective action.
    }

    // --- 5. Two-Week Block Check: Ensure the event stays within its bi-weekly block ---
    const weekOfOriginalEvent = originalEventStartTime.week();
    const weekOfOrderStart = orderStartDate.week();

    // Obscure business rule: if the event was in the second week of the order, any move is permissible.
    if (weekOfOriginalEvent - weekOfOrderStart === 1) {
      return false; // Valid move, based on the original logic's special case.
    }

    // Define the valid two-week period robustly to handle year-end boundaries.
    const startOfValidPeriod = originalEventStartTime.clone().startOf("week");
    const endOfValidPeriod = startOfValidPeriod.clone().add(2, "weeks");

    // The move is invalid if the new event time is outside this two-week window.
    const isWithinValidPeriod = newEventStartTime.isBetween(
      startOfValidPeriod,
      endOfValidPeriod,
      null,
      "[)"
    );

    return !isWithinValidPeriod;
  }

  extractNumbers(description) {
    if (typeof description !== "string") {
      console.error("Input must be a string.");
      return {
        firstNumber: null,
        numberInParentheses: null,
      };
    }

    // Regular expression to find the first sequence of one or more digits.
    const firstMatch = description.match(/\d+/);

    // Regular expression to find digits inside parentheses.
    // The '(\d+)' part is a capturing group for the digits themselves.
    const parenthesisMatch = description.match(/\((\d+)/);

    // Parse the matched strings into integers, or set to null if no match was found.
    const firstNumber = firstMatch ? parseInt(firstMatch[0], 10) : null;
    const numberInParentheses = parenthesisMatch
      ? parseInt(parenthesisMatch[1], 10)
      : null;

    return {
      firstNumber,
      numberInParentheses,
    };
  }
}



// WEBPACK FOOTER //
// ./src/providers/calendar-service/calendar-service.ts